<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import {
  AVAILABLE_CUSTOM_ROLE_PERMISSIONS,
  MANAGE_ALL_CONVERSATION_PERMISSIONS,
  CONVERSATION_UNASSIGNED_PERMISSIONS,
  CONVERSATION_PARTICIPATING_PERMISSIONS,
  CUSTOM_ROLE_PERMISSION_GROUPS,
  CUSTOM_ROLE_HIDDEN_PERMISSIONS,
  sortCustomRolePermissions,
} from 'dashboard/constants/permissions.js';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedRole: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const name = ref('');
const description = ref('');
const selectedPermissions = ref([]);

const nameInput = ref(null);

const addCustomRole = reactive({
  showLoading: false,
  message: '',
});

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
  description: { required },
  selectedPermissions: { required, minLength: minLength(1) },
}));

const v$ = useVuelidate(rules, { name, description, selectedPermissions });

const permissionGroups = computed(() =>
  CUSTOM_ROLE_PERMISSION_GROUPS.map(group => ({
    key: group.key,
    label: t(`CUSTOM_ROLE.PERMISSION_GROUPS.${group.key}.TITLE`),
    description: t(`CUSTOM_ROLE.PERMISSION_GROUPS.${group.key}.DESCRIPTION`),
    permissions: sortCustomRolePermissions(
      group.permissions.filter(
        permission =>
          AVAILABLE_CUSTOM_ROLE_PERMISSIONS.includes(permission) &&
          !CUSTOM_ROLE_HIDDEN_PERMISSIONS.includes(permission)
      )
    ),
  })).filter(group => group.permissions.length)
);

const resetForm = () => {
  name.value = '';
  description.value = '';
  selectedPermissions.value = [];
  v$.value.$reset();
};

const populateEditForm = () => {
  name.value = props.selectedRole.name || '';
  description.value = props.selectedRole.description || '';
  selectedPermissions.value = (props.selectedRole.permissions || []).filter(
    permission => !CUSTOM_ROLE_HIDDEN_PERMISSIONS.includes(permission)
  );
};

watch(
  selectedPermissions,
  (newValue, oldValue) => {
    // Check if manage all conversation permission is added or removed
    const hasAddedManageAllConversation =
      newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);
    const hasRemovedManageAllConversation =
      oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);

    if (hasAddedManageAllConversation) {
      // If manage all conversation permission is added,
      // then add unassigned and participating permissions automatically
      selectedPermissions.value = [
        ...new Set([
          ...selectedPermissions.value,
          CONVERSATION_UNASSIGNED_PERMISSIONS,
          CONVERSATION_PARTICIPATING_PERMISSIONS,
        ]),
      ];
    } else if (hasRemovedManageAllConversation) {
      // If manage all conversation permission is removed,
      // then only remove manage all conversation permission
      selectedPermissions.value = selectedPermissions.value.filter(
        p => p !== MANAGE_ALL_CONVERSATION_PERMISSIONS
      );
    }
  },
  { deep: true }
);

onMounted(() => {
  if (props.mode === 'edit') {
    populateEditForm();
  }
  // Focus the name input when mounted
  nameInput.value?.focus();
});

const getTranslationKey = base => {
  return props.mode === 'edit'
    ? `CUSTOM_ROLE.EDIT.${base}`
    : `CUSTOM_ROLE.ADD.${base}`;
};

const modalTitle = computed(() => t(getTranslationKey('TITLE')));
const modalDescription = computed(() => t(getTranslationKey('DESC')));
const submitButtonText = computed(() => t(getTranslationKey('SUBMIT')));

const handleCustomRole = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  addCustomRole.showLoading = true;
  try {
    const roleData = {
      name: name.value,
      description: description.value,
      permissions: selectedPermissions.value,
    };

    if (props.mode === 'edit') {
      await store.dispatch('customRole/updateCustomRole', {
        id: props.selectedRole.id,
        ...roleData,
      });
      useAlert(t('CUSTOM_ROLE.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('customRole/createCustomRole', roleData);
      useAlert(t('CUSTOM_ROLE.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message || t(`CUSTOM_ROLE.FORM.API.ERROR_MESSAGE`);
    useAlert(errorMessage);
  } finally {
    addCustomRole.showLoading = false;
  }
};

const isSubmitDisabled = computed(
  () => v$.value.$invalid || addCustomRole.showLoading
);
</script>

<template>
  <div
    class="custom-role-modal flex flex-col h-auto w-full max-w-full overflow-x-hidden"
  >
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="modalDescription"
    />
    <form class="flex flex-col w-full" @submit.prevent="handleCustomRole">
      <div class="w-full">
        <label :class="{ error: v$.name.$error }">
          {{ $t('CUSTOM_ROLE.FORM.NAME.LABEL') }}
          <input
            ref="nameInput"
            v-model.trim="name"
            type="text"
            :placeholder="$t('CUSTOM_ROLE.FORM.NAME.PLACEHOLDER')"
            @blur="v$.name.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.description.$error }">
          {{ $t('CUSTOM_ROLE.FORM.DESCRIPTION.LABEL') }}

          <textarea
            v-model="description"
            :rows="3"
            :placeholder="$t('CUSTOM_ROLE.FORM.DESCRIPTION.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
        </label>
      </div>

      <div class="w-full space-y-4">
        <p
          :class="{ 'text-n-ruby-9': v$.selectedPermissions.$error }"
          class="text-sm font-semibold text-n-slate-12"
        >
          {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.LABEL') }}
        </p>
        <div
          v-for="group in permissionGroups"
          :key="group.key"
          class="rounded-xl border border-n-weak/60 bg-n-solid-1 px-4 py-3 shadow-sm dark:border-white/20 dark:bg-white/10"
        >
          <div
            class="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between"
          >
            <p class="text-sm font-semibold text-n-slate-12">
              {{ group.label }}
            </p>
            <p
              v-if="group.description"
              class="text-xs text-n-slate-9 break-words sm:max-w-[60%] sm:text-right"
            >
              {{ group.description }}
            </p>
          </div>
          <div class="mt-3 grid gap-2 sm:grid-cols-2">
            <label
              v-for="permission in group.permissions"
              :key="permission"
              class="flex min-w-0 items-start gap-2 text-sm text-n-slate-12"
            >
              <input
                :id="`${group.key}-${permission}`"
                v-model="selectedPermissions"
                type="checkbox"
                :value="permission"
                name="permissions"
                class="mt-0.5 size-4 flex-shrink-0 accent-black"
              />
              <span class="min-w-0 break-words leading-5">{{
                $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`)
              }}</span>
            </label>
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('CUSTOM_ROLE.FORM.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="submitButtonText"
          :disabled="isSubmitDisabled"
          :is-loading="addCustomRole.showLoading"
        />
      </div>
    </form>
  </div>
</template>

<style scoped>
.custom-role-modal {
  background: #ffffff;
}

:deep(form) {
  padding: 0 2rem 1rem;
}

@media (max-width: 640px) {
  :deep(form) {
    padding: 0 1rem 0.875rem;
  }
}

/* Override dark tokens from woot-modal-header and base inputs */
:deep(.modal-header) {
  background: #ffffff !important;
}
:deep(.modal-body),
:deep(.modal-footer) {
  background: #ffffff !important;
}
:deep(.modal-container) {
  background: #ffffff !important;
}

/* Labels */
:deep(label),
:deep(label > span),
:deep(label > div) {
  color: #111827 !important;
}

/* Inputs, textareas */
:deep(input:not(.reset-base)),
:deep(textarea:not(.reset-base)) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}
:deep(input:focus),
:deep(textarea:focus) {
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1) !important;
}
:deep(input::placeholder),
:deep(textarea::placeholder) {
  color: #9ca3af !important;
}

/* Permission group titles */
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2),
:deep(.bg-n-background) {
  background: #ffffff !important;
}
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}

/* Checkbox group cards */
:deep([class*='rounded'][class*='border']) {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
}

/* Error messages */
:deep(.message) {
  color: #ef4444 !important;
}
</style>
